import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

import { NotificationType } from './models/notification.enum';

/**
 * Namespace the toast icons are registered under, so `<mat-icon svgIcon="…">`
 * never collides with icons registered elsewhere in the app.
 */
export const NOTIFICATION_ICON_NAMESPACE = 'du-toast';

export const NOTIFICATION_CLOSE_ICON = 'close';

/**
 * Stroked 24×24 glyphs matching the design. Inlined rather than pulled from the
 * Material Icons font so the toast renders identically before webfonts load and
 * in environments where the font is blocked.
 */
const ICON_LITERALS: Readonly<Record<string, string>> = {
  [NotificationType.Warning]:
    '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/>' +
    '<path d="M12 9v4.5"/><path d="M12 17.25h.01"/>',

  [NotificationType.Error]:
    '<circle cx="12" cy="12" r="9"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/>',

  [NotificationType.Info]:
    '<circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><path d="M12 8h.01"/>',

  [NotificationType.Success]:
    '<path d="M21 12a9 9 0 1 1-5.2-8.16"/><path d="m8.75 11.75 2.75 2.75L21.5 4.5"/>',

  [NOTIFICATION_CLOSE_ICON]: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
};

const SVG_ATTRS =
  'xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" ' +
  'stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"';

let registered = false;

/**
 * Registers every toast glyph once per application. Safe to call from each
 * toast instance — subsequent calls are no-ops.
 */
export function registerNotificationIcons(
  iconRegistry: MatIconRegistry,
  sanitizer: DomSanitizer
): void {
  if (registered) {
    return;
  }
  registered = true;

  Object.keys(ICON_LITERALS).forEach((name) => {
    iconRegistry.addSvgIconLiteralInNamespace(
      NOTIFICATION_ICON_NAMESPACE,
      name,
      sanitizer.bypassSecurityTrustHtml(`<svg ${SVG_ATTRS}>${ICON_LITERALS[name]}</svg>`)
    );
  });
}

/** Maps a severity onto its registered glyph; `Custom` falls back to the info mark. */
export function notificationIconName(type: NotificationType): string {
  return type === NotificationType.Custom ? NotificationType.Info : type;
}
